import React, { forwardRef, useState } from 'react';
import { Dialog, AppBar, Toolbar, Slide, Typography, Box } from '@mui/material';
import { Close, Edit } from '@mui/icons-material';
import { TransitionProps } from '@mui/material/transitions';

import { deleteHtmlFromString } from 'helpers/htmlParser';
import { Job } from '..';
import PrimaryButton from 'components/Common/PrimaryButton';
import Editor from 'components/Common/TextEditor';
import { doc, updateDoc, addDoc, collection, Timestamp } from 'firebase/firestore';
import { db } from '../../../utils/config/firebase';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { errorLogger } from 'utils/config/firebase/logs';

const Transition = forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const FullScreenDialog: React.FC<{
    editMode?: boolean;
    initialIntakeNote?: string;
    initialJobPost?: string;
    job?: Job;
}> = ({ editMode, initialIntakeNote: initialIntakeNote, initialJobPost, job }) => {
    const [intakeNoteValue, setIntakeNoteValue] = useState<string>(
        editMode ? initialIntakeNote! : '',
    );
    const [jobPostValue, setJobPostValue] = useState<string>(editMode ? initialJobPost! : '');
    const [open, setOpen] = useState(false);
    const params = useParams();

    const canSave =
        deleteHtmlFromString(intakeNoteValue).length > 0 &&
        deleteHtmlFromString(jobPostValue).length > 0;

    const handleIntakeNoteChange = (value: string) => setIntakeNoteValue(value);
    const handleJobPostChange = (value: string) => setJobPostValue(value);
    const handleClickOpen = () => setOpen(true);

    const handleClose = () => {
        setIntakeNoteValue(editMode ? initialIntakeNote! : '');
        setJobPostValue(editMode ? initialJobPost! : '');
        setOpen(false);
    };

    const handleSave = async () => {
        const collectionPath = `/jobAdsTrainingData/${params.collection_id}/examples`;
        const data: {
            intake_note: string;
            job_post: string;
            createdDate?: Timestamp; // Make createdDate optional
        } = {
            intake_note: intakeNoteValue,
            job_post: jobPostValue,
        };
        let docRef;
        if (editMode) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            docRef = doc(db, collectionPath, job.id);
            await updateDoc(docRef, data);
        } else {
            data.createdDate = Timestamp.now();
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            docRef = await addDoc(collection(db, collectionPath), data);
            setIntakeNoteValue('');
            setJobPostValue('');
        }

        preprocessExample(docRef.id);
        setOpen(false);
    };

    const preprocessExample = (documentId: string) => {
        axios({
            url: 'https://prepare-data-w45i3ex62a-ey.a.run.app/trainer/v1/',
            method: 'post',
            data: {
                document_id: documentId,
                opco: params.collection_id,
            },
        })
            .then((r) => {
                console.log(r);
            })
            .catch((error) => {
                if (error instanceof Error) {
                    errorLogger.critical(error?.message, 'preprocessExample');
                }
                console.log(error);
            });
    };

    return (
        <Box>
            {!editMode && (
                <PrimaryButton className="mr-10 mt-10" text="ADD NEW" onClick={handleClickOpen} />
            )}
            {editMode && <Edit sx={{ cursor: 'pointer' }} onClick={handleClickOpen} />}
            <Dialog
                className="model-trainer-dialog"
                fullScreen
                open={open}
                onClose={handleClose}
                TransitionComponent={Transition}
            >
                <AppBar position="static">
                    <Toolbar className="flex justify-end app-bar">
                        <Box className="flex align-center">
                            <Close onClick={handleClose} sx={{ cursor: 'pointer' }} />
                        </Box>
                    </Toolbar>
                </AppBar>
                <Box className="panel-container flex">
                    <Box className="editors-container flex justify-center">
                        <Box className="intake-notes">
                            <Box className="header">
                                <Typography className="ml-40" variant="h6">
                                    Intake Notes
                                </Typography>
                            </Box>
                            <Editor value={intakeNoteValue} onChange={handleIntakeNoteChange} />
                        </Box>
                        <Box className="output">
                            <Box className="header">
                                <Typography className="ml-40" variant="h6">
                                    Expected Output
                                </Typography>
                            </Box>
                            <Editor
                                placeholder="Paste the expected output "
                                value={jobPostValue}
                                onChange={handleJobPostChange}
                            />
                        </Box>
                    </Box>
                    <PrimaryButton
                        disabled={!canSave}
                        className="save-btn"
                        text="Save"
                        onClick={handleSave}
                    />
                </Box>
            </Dialog>
        </Box>
    );
};

export default FullScreenDialog;
